import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaChartLine,
  FaUsers,
  FaHome,
  FaCalendarCheck,
  FaRupeeSign,
  FaHistory,
  FaBell,
  FaSignOutAlt,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { WorkspaceProvider, useWorkspace } from '../../context/WorkspaceContext';
import './WorkspaceLayout.css';

const ALL_NAV = [
  { to: '/workspace', end: true, icon: FaChartLine, label: 'Performance', permission: 'view_dashboard' },
  { to: '/workspace/staff', icon: FaUsers, label: 'Staff management', permission: 'manage_staff' },
  { to: '/workspace/properties', icon: FaHome, label: 'Properties', permission: 'view_properties' },
  { to: '/workspace/bookings', icon: FaCalendarCheck, label: 'Bookings', permission: 'manage_bookings' },
  { to: '/workspace/analytics', icon: FaRupeeSign, label: 'Revenue', permission: 'view_analytics' },
  { to: '/workspace/activity', icon: FaHistory, label: 'Activity logs', permission: 'view_activity' },
  { to: '/workspace/notifications', icon: FaBell, label: 'Notifications', permission: 'view_notifications' },
];

const WorkspaceLayoutInner = () => {
  const navigate = useNavigate();
  const { permissions } = useWorkspace();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  const readUser = () => {
    try {
      setUser(JSON.parse(localStorage.getItem('user') || 'null'));
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    readUser();
    window.addEventListener('workspace-user-updated', readUser);
    return () => window.removeEventListener('workspace-user-updated', readUser);
  }, []);

  const navItems = useMemo(() => {
    const hasApiPerms = Object.keys(permissions || {}).length > 0;
    if (hasApiPerms) {
      return ALL_NAV.filter((item) => permissions[item.permission]);
    }
    const role = String(user?.role || '');
    const fallback = {
      company_admin: new Set(ALL_NAV.map((n) => n.permission)),
      company_manager: new Set(['view_dashboard', 'view_properties', 'manage_bookings', 'view_analytics', 'view_activity', 'view_notifications']),
      company_staff: new Set(['view_dashboard', 'view_properties', 'manage_bookings', 'view_notifications']),
    };
    const perms = fallback[role] || new Set();
    return ALL_NAV.filter((item) => perms.has(item.permission));
  }, [permissions, user]);

  const companyName = user?.company?.name || user?.company?.profile?.displayName || 'Company';
  const companyInitial = companyName.charAt(0).toUpperCase();

  return (
    <div className="workspace-layout">
      <div
        className={`workspace-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <aside className={`workspace-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="workspace-brand">
          <div className="workspace-brand-row">
            <span className="workspace-brand-mark" aria-hidden="true">
              {companyInitial}
            </span>
            <div>
              <h2>Workspace</h2>
              <p>{companyName}</p>
            </div>
          </div>
        </div>
        <nav className="workspace-nav" aria-label="Workspace navigation">
          <span className="workspace-nav-label">Menu</span>
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setSidebarOpen(false)}>
              <span className="workspace-nav-icon">
                <item.icon />
              </span>
              <span className="workspace-nav-text">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="workspace-sidebar-footer">
          <a href="/host" onClick={() => setSidebarOpen(false)}>
            <FaExternalLinkAlt /> Host tools
          </a>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate('/');
            }}
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </aside>
      <div className="workspace-main">
        <header className="workspace-header">
          <button
            type="button"
            className="workspace-menu-btn"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Menu"
          >
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <div className="workspace-header-meta">
            <span className="workspace-header-avatar" aria-hidden="true">
              {companyInitial}
            </span>
            <div>
              <span className="workspace-header-kicker">Company workspace</span>
              <span className="workspace-header-title">{companyName}</span>
            </div>
          </div>
        </header>
        <main className="workspace-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const WorkspaceLayout = () => (
  <WorkspaceProvider>
    <WorkspaceLayoutInner />
  </WorkspaceProvider>
);

export default WorkspaceLayout;
